import tinycolor from 'tinycolor2'

import { allcolors } from '../constants/CSScolors'

/**
 * @typedef {import('tinycolor2').ColorFormats.RGB} RGB
 */

const sqr = (x) => x * x

/**
 * Produce the euclidean ditsance between the RGB components in the
 * colors one and other.
 * @param {RGB} one
 * @param {RGB} other
 * @return {number}
 */
const euclidean = (one, other) => {
  return Math.sqrt(sqr(one.r - other.r) + sqr(one.g - other.g) + sqr(one.b - other.b))
}

/**
 * Produce the color in {@link allColors} that's 60 degrees more than
 * the last color and not yet used.
 *
 * @param {Array<String>} usedColors
 * @return {String}
 */
const closestUnusedTo60DegreesMore = (usedColors) => {
  const latestColourHSV = tinycolor(usedColors[0]).toHsv()
  const nextAnchor = tinycolor({
    ...latestColourHSV,
    h: (latestColourHSV.h + 60) % 360,
  }).toRgb()
  const usedColorsSet = new Set(usedColors)
  return allcolors.reduce(
    (acc, otherColor) => {
      if (!usedColorsSet.has(otherColor)) {
        const tinyOtherColor = tinycolor(otherColor)
        const otherLightness = tinyOtherColor.toHsl().l
        if (otherLightness >= 0.3 && otherLightness <= 0.7) {
          const { distance } = acc
          const newDistance = euclidean(nextAnchor, tinycolor(otherColor).toRgb())
          if (newDistance < distance) {
            return {
              distance: newDistance,
              closest: otherColor,
            }
          } else {
            return acc
          }
        } else {
          return acc
        }
      } else {
        return acc
      }
    },
    { distance: Number.POSITIVE_INFINITY, closest: '#0b1117' }
  ).closest
}

/**
 * @param {number | 'default'} length
 * @param {Array<String>} [colorsInBook]
 * @return {String}
 */
export function nextColor(length, colorsInBook) {
  switch (length) {
    case 0:
      return '#6cace4' // blue
    case 1:
      return '#78be20' // green
    case 2:
      return '#e5554f' // red
    case 3:
      return '#ff7f32' // orange
    case 4:
      return '#ffc72c' // yellow
    default:
      if (colorsInBook) {
        return closestUnusedTo60DegreesMore(colorsInBook)
      } else {
        return '#0b1117' // black
      }
  }
}

/**
 * @param {number | 'default'} length
 * @param {Array<String>} [colorsInBook]
 */
export function nextDarkColor(length, colorsInBook) {
  switch (length) {
    case 0:
      return '#ccc'
    case 1:
      return '#baed79'
    case 2:
      return '#ffb8b5'
    case 3:
      return '#ffac7a'
    case 4:
      return '#fff373'
    default:
      if (colorsInBook) {
        return closestUnusedTo60DegreesMore(colorsInBook)
      } else {
        return '#0b1117' // black
      }
  }
}

export function lightBackground(color) {
  return tinycolor(color).setAlpha(0.1).toHslString()
}
